/* @flow */
// Ads & announcements panel (replaces the old Promotions widget).
// - the gym's ads (ready-made promotions + ones made in the Ad Studio), with on/off switches
// - how often ad breaks play (Light / Normal / Busy)
// - Create ad -> making -> review (listen, edit script, try again) -> approve and go live
import React, { Component } from 'react'
import zones from '../../stores/Zones'
import { observer, inject } from 'mobx-react'
import Switch from 'rc-switch'
import 'rc-switch/assets/index.css'
import './AdsPanel.css'
import { VOICES, PlayIcon, PauseIcon, BackIcon, MegaphoneIcon, EditIcon, TrashIcon } from './shared'
import { readDocumentFile } from '../../helpers/docx'

const MOODS = [['energetic', 'Energetic'], ['upbeat', 'Upbeat'], ['chilled', 'Chilled']]
const FREQUENCIES_CLASSIC = [
  ['light', 'Light', 'One of your ads about every 7 songs (around 2 an hour)'],
  ['normal', 'Normal', 'One of your ads about every 4 songs (around 4 an hour)'],
  ['busy', 'Busy', 'One of your ads about every 2 songs (around 7 an hour)']
]

const FREQUENCIES = [
  ['light', 'Light', '2'],
  ['normal', 'Normal', '4'],
  ['busy', 'Busy', '7']
]

const STEPS = [
  ['reading', 'Reading your website'],
  ['writing', 'Writing the script'],
  ['recording', 'Recording the voice'],
  ['mixing', 'Adding the music'],
  ['levelling', 'Matching the volume to your playlist']
]

const fmtDuration = (s) => {
  const n = Math.round(Number(s) || 0)
  return n ? `0:${String(n).padStart(2, '0')}` : ''
}
const fmtDate = (iso) => {
  if (!iso) return ''
  const d = new Date(`${iso}T12:00:00`)
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}
const inDays = (n) => {
  const d = new Date(Date.now() + n * 86400000)
  return d.toISOString().slice(0, 10)
}


class AdsPanel extends Component {
  state = {
    details: '',
    website: '',
    voice: 'Tiffany',
    mood: 'energetic',
    until: '',
    script: '',
    playingKey: '',
    busy: false,
    showLibrary: false,
    showMine: false,
    openActions: '',   // uuid of the ad whose Edit / Delete row is open
    confirmDelete: '', // uuid waiting for a second tap to delete
    title: '',
    // Ad pack (make several ads from a website or a document)
    packSource: 'website',   // website | doc | file | text
    packWebsite: '',
    packDocUrl: '',
    packText: '',
    packFileName: '',
    packFileError: '',
    packVoice: 'Tiffany',
    packMood: 'energetic',
    packPicked: null,        // { [suggestion id]: true }
    packEdits: {},           // { [suggestion id]: { title, brief } }
    reviewEdits: {}          // Review all: { [draft id]: { title, script, voice, mood } }
  }

  audio = null

  componentDidMount () {
    this.props.studioStore.load()
  }

  componentDidUpdate (prevProps) {
    const { studioStore } = this.props
    // Each screen is a different height, so when it changes keep the panel's
    // heading in view instead of leaving the page scrolled past it.
    if (studioStore.screen !== this.lastScreen) {
      const first = this.lastScreen === undefined
      this.lastScreen = studioStore.screen
      if (!first && this.root && this.root.getBoundingClientRect) {
        const top = this.root.getBoundingClientRect().top
        if (top < 0 || top > window.innerHeight * 0.6) {
          try {
            window.scrollBy({ top: top - 96, behavior: 'smooth' })
          } catch (e) { window.scrollBy(0, top - 96) }
        }
      }
    }
    const draft = studioStore.draft
    // Put the latest script in the editor whenever a new version is ready
    if (draft && draft.step === 'ready' && draft.script && draft.updated !== this.lastDraftVersion) {
      this.lastDraftVersion = draft.updated
      this.setState({ script: draft.script, title: draft.title || '', reviewVoice: draft.voice || 'Tiffany', reviewMood: draft.mood || 'energetic' })
    }
  }

  componentWillUnmount () {
    this.stopPreview()
    this.props.studioStore.stopPolling()
    this.props.studioStore.stopReviewPolling()
  }

  // ---- preview audio
  preview (key, url) {
    if (!url) return
    if (this.state.playingKey === key) {
      this.stopPreview()
      return
    }
    // Music zones: a device playing a zone would send the preview out of that
    // area's speakers, so ask first (once per visit)
    if (zones.speakerCheck(() => this.preview(key, url))) return
    this.stopPreview()
    this.props.onPreviewStart && this.props.onPreviewStart()
    this.audio = new Audio(url)
    this.audio.onended = () => this.setState({ playingKey: '' })
    this.audio.play().catch(() => this.setState({ playingKey: '' }))
    this.setState({ playingKey: key })
  }

  stopPreview () {
    if (this.audio) {
      this.audio.pause()
      this.audio = null
    }
    if (this.state.playingKey) this.setState({ playingKey: '' })
  }

  // ---- actions
  requirePro (fn) {
    const { userStore, onSubscribe } = this.props
    if (!userStore.isPremium) {
      onSubscribe && onSubscribe()
      return
    }
    fn()
  }

  openCreate () {
    const { studioStore } = this.props
    this.requirePro(() => {
      this.stopPreview()
      this.setState({ website: this.state.website || studioStore.website, details: '', until: '' })
      studioStore.openCreate()
    })
  }

  submit (e) {
    e.preventDefault()
    const { details, voice, mood, until } = this.state
    // A website ("mygym.ie") or just the gym's name: the music service works out which
    const website = this.state.website.trim()
    this.props.studioStore.create({ kind: 'Announcement', details, website, voice, mood, until })
  }

  // Edit (re-record) or delete one of the gym's own ads from the list
  editAd (info) {
    this.requirePro(() => {
      this.stopPreview()
      this.setState({ openActions: '', confirmDelete: '' })
      this.props.studioStore.openDraft(info.id)
    })
  }

  deleteAd (info, uuid) {
    if (this.state.confirmDelete !== uuid) {
      this.setState({ confirmDelete: uuid })
      return
    }
    this.stopPreview()
    this.setState({ openActions: '', confirmDelete: '' })
    this.props.studioStore.archive(info.id)
  }

  renderAdItem (m) {
    const { studioStore, onToggleMessage } = this.props
    const { playingKey, openActions, confirmDelete } = this.state

    const info = studioStore.infoFor(m.uuid)
    // The section already says which kind it is, so the line under the title only adds details
    const meta = info
      ? [info.voice, fmtDuration(info.duration), info.until ? `until ${fmtDate(info.until)}` : '']
        .filter(Boolean).join(' · ')
      : ''
    const open = info && openActions === m.uuid
    return (
      <li key={ m.uuid } className={ `studio__item ${info ? 'studio__item--mine' : ''} ${open ? 'studio__item--open' : ''}` }>
        <div className="studio__item-row">
          <button
            type="button"
            className="studio__play"
            aria-label={ playingKey === m.uuid ? `Stop ${m.title}` : `Listen to ${m.title}` }
            disabled={ !m.url }
            onClick={ () => this.preview(m.uuid, m.url) }
          >{ playingKey === m.uuid ? <PauseIcon /> : <PlayIcon /> }</button>
          <div className="studio__item-text">
            <span className="studio__item-title">{ m.title }</span>
            { meta && <span className="studio__item-meta">{ meta }</span> }
          </div>
          {
            info && (
              <button
                type="button"
                className={ `studio__more ${open ? 'is-active' : ''}` }
                aria-label={ open ? `Hide options for ${m.title}` : `Options for ${m.title}` }
                aria-expanded={ open }
                onClick={ () => this.setState({ openActions: open ? '' : m.uuid, confirmDelete: '' }) }
              >···</button>
            )
          }
          <Switch
            checked={ !!m.enabled }
            onChange={ (value) => onToggleMessage && onToggleMessage(m, value) }
            aria-label={ `Play "${m.title}"` }
          />
        </div>
        {
          open && (
            <div className="studio__item-actions">
              <button type="button" className="studio__btn studio__btn--ghost studio__btn--small" onClick={ () => this.editAd(info) }>
                <EditIcon /> Edit or re-record
              </button>
              <button type="button" className={ `studio__btn studio__btn--small ${confirmDelete === m.uuid ? 'studio__btn--danger' : 'studio__btn--ghost'}` } onClick={ () => this.deleteAd(info, m.uuid) }>
                <TrashIcon /> { confirmDelete === m.uuid ? 'Tap again to delete' : 'Delete' }
              </button>
            </div>
          )
        }
      </li>
    )
  }

  // ---- screens
  renderList () {
    const { studioStore, audioMessageStore } = this.props
    const freq = FREQUENCIES_CLASSIC.find((f) => f[0] === studioStore.frequency) || FREQUENCIES_CLASSIC[1]
    // Two folders, by kind: the gym's own ads ("Made for you") and the ready-made library.
    // An ad stays in its folder, in the same place, whether it is on or off, so the list
    // never moves under your finger while you switch ads on and off.
    const all = audioMessageStore.audioMessages || []
    const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''))
    const mine = all.filter((m) => studioStore.infoFor(m.uuid))
    // Deleted ads can linger in the catalogue for a while: never show them as stock
    const hidden = studioStore.hidden || []
    const library = all.filter((m) => !studioStore.infoFor(m.uuid) && hidden.indexOf(m.uuid) < 0).slice().sort(byTitle)
    const { showLibrary, showMine } = this.state
    // Approved ads take a minute to reach the catalogue; show them meanwhile
    const goingLive = studioStore.ads.filter((a) => a.status === 'live' && a.uuid && !all.some((m) => m.uuid === a.uuid))
    const mineOn = mine.filter((m) => m.enabled).length + goingLive.length
    const libraryOn = library.filter((m) => m.enabled).length
    const drafts = studioStore.drafts

    return (
      <div className="studio__screen">
        <header className="studio__header">
          <span className="studio__icon"><MegaphoneIcon /></span>
          <h2 className="studio__headline">Ads</h2>
        </header>

        {
          studioStore.justWentLive && (
            <div className="studio__notice" role="status">
              "{ studioStore.justWentLive }" is live. It joins the ad breaks in a minute or two.
            </div>
          )
        }
        { studioStore.error && <div className="studio__error" role="alert">{ studioStore.error }</div> }

        <div className="studio__group">
          <span className="studio__label" id="studio-per-hour">Per hour</span>
          <div className="studio__segmented studio__segmented--perhour" role="group" aria-labelledby="studio-per-hour">
            {
              FREQUENCIES.map(([value, label, perHour]) => (
                <button
                  key={ value }
                  type="button"
                  aria-pressed={ studioStore.frequency === value }
                  className={ `studio__segment ${studioStore.frequency === value ? 'is-active' : ''}` }
                  onClick={ () => this.requirePro(() => studioStore.setFrequency(value)) }
                  aria-label={ `${label}, about ${perHour} an hour` }
                >{ label }<span className="studio__segment-num">{ perHour }</span></button>
              ))
            }
          </div>
          <span className="studio__hint">{ freq[2] }</span>
        </div>

        <button type="button" className="studio__pack-cta studio__pack-cta--new" onClick={ () => this.openCreate() }>
          <span className="studio__pack-cta-title">+ New single ad</span>
          <span className="studio__pack-cta-text">Tell us what to promote and we write, voice and mix one ad in about a minute.</span>
        </button>

        <button type="button" className="studio__pack-cta" onClick={ () => this.openPack() }>
          <span className="studio__pack-cta-title">+ Make several ads at once</span>
          <span className="studio__pack-cta-text">From your website, a Google Doc, a Word file or pasted text. We propose the ads, you tick the ones you want.</span>
        </button>

        {
          drafts.length > 1 && (
            <button type="button" className="studio__btn studio__btn--primary" onClick={ () => this.requirePro(() => { this.stopPreview(); studioStore.openReviewAll() }) }>
              { studioStore.readyDrafts.length === drafts.length
                ? `Review and approve all ${drafts.length} drafts`
                : `Review drafts (${studioStore.readyDrafts.length} of ${drafts.length} ready)` }
            </button>
          )
        }

        {
          drafts.length > 0 && (
            <ul className="studio__list" aria-label="Drafts">
              {
                drafts.map((d) => (
                  <li key={ d.id } className="studio__item studio__item--draft">
                    <div className="studio__item-text">
                      <span className="studio__item-title">{ d.title || 'New ad' }</span>
                      <span className="studio__item-meta">
                        { d.step === 'ready' ? 'Draft ready to review' : d.step === 'failed' ? "Couldn't be made" : 'Being made…' }
                      </span>
                    </div>
                    <button type="button" className="studio__btn studio__btn--ghost studio__btn--small" onClick={ () => studioStore.openDraft(d.id) }>
                      { d.step === 'ready' ? 'Review' : 'Open' }
                    </button>
                  </li>
                ))
              }
            </ul>
          )
        }

        <section className="studio__section studio__section--mine" aria-labelledby="studio-mine">
          <button
            type="button"
            className="studio__section-head studio__section-head--toggle"
            aria-expanded={ showMine }
            onClick={ () => this.setState({ showMine: !showMine }) }
          >
            <span className="studio__section-text">
              <h3 id="studio-mine" className="studio__section-title">Your ads</h3>
              <span className="studio__section-count">{ `${studioStore.made} of ${studioStore.limit}` }{ mineOn ? ` · ${mineOn} on` : ' · none on' }</span>
            </span>
            <span className="studio__section-chevron" aria-hidden="true">{ showMine ? '−' : '+' }</span>
          </button>
          { showMine && (
          <ul className="studio__list" aria-label="Your ads">
            {
              goingLive.map((a) => (
                <li key={ a.uuid } className="studio__item studio__item--mine">
                  <div className="studio__item-row">
                    <button type="button" className="studio__play" disabled aria-label="Available in a moment"><PlayIcon /></button>
                    <div className="studio__item-text">
                      <span className="studio__item-title">{ a.title }</span>
                      <span className="studio__item-meta">Going live · joins the ad breaks in a minute</span>
                    </div>
                    <Switch checked disabled aria-label={ `"${a.title}" is going live` } />
                  </div>
                </li>
              ))
            }
            { mine.map((m) => this.renderAdItem(m)) }
            { mine.length === 0 && goingLive.length === 0 && drafts.length === 0 && (
              <li className="studio__empty">No ads of your own yet. Tap “+ New ad” and we'll write, voice and mix one in about a minute.</li>
            ) }
          </ul>
          ) }
          { showMine && mine.length > 0 && <p className="studio__hint studio__hint--tight">Tap ··· on an ad to re-record it or delete it.</p> }
        </section>

        {
          library.length > 0 && (
            <section className="studio__section studio__section--stock" aria-labelledby="studio-stock">
              <button
                type="button"
                className="studio__section-head studio__section-head--toggle"
                aria-expanded={ showLibrary }
                onClick={ () => this.setState({ showLibrary: !showLibrary }) }
              >
                <span className="studio__section-text">
                  <h3 id="studio-stock" className="studio__section-title">Stock ads</h3>
                  <span className="studio__section-count">{ `${library.length} ready-made by GymRadio.com` }{ libraryOn ? ` · ${libraryOn} on` : ' · none on' }</span>
                </span>
                <span className="studio__section-chevron" aria-hidden="true">{ showLibrary ? '−' : '+' }</span>
              </button>
              {
                showLibrary && (
                  <React.Fragment>
                    <p className="studio__hint studio__hint--tight">General gym messages by GymRadio.com. Switch on the ones that suit your gym.</p>
                    <ul className="studio__list" aria-label="Stock ads">
                      { library.map((m) => this.renderAdItem(m)) }
                    </ul>
                  </React.Fragment>
                )
              }
            </section>
          )
        }

      </div>
    )
  }

  // ---- Ad pack
  openPack () {
    const { studioStore } = this.props
    this.requirePro(() => {
      this.stopPreview()
      this.setState({ packWebsite: this.state.packWebsite || studioStore.website || '', packPicked: null, packEdits: {}, packFileError: '' })
      studioStore.openPack()
    })
  }

  async pickFile (e) {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    this.setState({ packFileError: '', packFileName: '' })
    try {
      const text = await readDocumentFile(file)
      if (text.length < 80) throw new Error("That file has very little text in it.")
      this.setState({ packText: text, packFileName: `${file.name} · ${text.split(/\s+/).length} words` })
    } catch (err) {
      this.setState({ packFileError: err.message || "That file couldn't be read." })
    }
  }

  readPack (e) {
    e.preventDefault()
    const { packSource, packWebsite, packDocUrl, packText } = this.state
    const { studioStore } = this.props
    this.setState({ packPicked: null, packEdits: {} })
    if (packSource === 'website') studioStore.readPack({ website: packWebsite.trim() })
    else if (packSource === 'doc') studioStore.readPack({ docUrl: packDocUrl.trim() })
    else studioStore.readPack({ text: packText.trim() })
  }

  packItems () {
    const { studioStore } = this.props
    const list = (studioStore.pack && studioStore.pack.suggestions) || []
    const { packPicked, packEdits } = this.state
    return list.map((sg) => {
      const edit = packEdits[sg.id] || {}
      return {
        ...sg,
        title: edit.title !== undefined ? edit.title : sg.title,
        brief: edit.brief !== undefined ? edit.brief : sg.brief,
        picked: packPicked ? !!packPicked[sg.id] : true
      }
    })
  }

  togglePick (id) {
    const picked = {}
    this.packItems().forEach((it) => { picked[it.id] = it.id === id ? !it.picked : it.picked })
    this.setState({ packPicked: picked })
  }

  editPick (id, field, value) {
    const packEdits = { ...this.state.packEdits, [id]: { ...(this.state.packEdits[id] || {}), [field]: value } }
    this.setState({ packEdits })
  }

  makePack () {
    const { studioStore } = this.props
    const items = this.packItems().filter((it) => it.picked && it.brief.trim().length >= 10)
      .map((it) => ({ title: it.title.trim(), brief: it.brief.trim(), until: it.until || '' }))
    const website = this.state.packSource === 'website' ? this.state.packWebsite.trim() : (studioStore.website || '')
    studioStore.makePack(items, this.state.packVoice, this.state.packMood, website)
  }

  renderPack () {
    const { studioStore } = this.props
    const pack = studioStore.pack
    const { packSource, packWebsite, packDocUrl, packText, packFileName, packFileError, packVoice, packMood } = this.state
    const back = (
      <button type="button" className="studio__back" aria-label="Back to your ads" onClick={ () => { this.stopPreview(); studioStore.backToList() } }><BackIcon /></button>
    )
    const free = Math.max(0, studioStore.limit - studioStore.made)

    // 1. Reading in progress
    if (pack && pack.status === 'reading') {
      return (
        <div className="studio__screen">
          <header className="studio__header">{ back }<h2 className="studio__title">Reading…</h2></header>
          <p className="studio__hint">We're reading { pack.source === 'website' ? 'your website' : pack.source === 'doc' ? 'the document' : 'your text' } and picking out what's worth announcing. Usually under a minute.</p>
          <div className="studio__progress" aria-hidden="true"><span style={ { width: '40%' } } /></div>
        </div>
      )
    }

    // 2. Proposals to tick
    if (pack && pack.status === 'done' && pack.suggestions && pack.suggestions.length) {
      const items = this.packItems()
      const count = items.filter((it) => it.picked && it.brief.trim().length >= 10).length
      return (
        <div className="studio__screen">
          <header className="studio__header">
            { back }
            <h2 className="studio__title">Here's what we found</h2>
          </header>
          <p className="studio__hint">Tick the ads you want and change any wording. Each becomes a draft you listen to and approve before it plays. You have { free } free { free === 1 ? 'slot' : 'slots' }.</p>
          <ul className="studio__picks" aria-label="Proposed ads">
            {
              items.map((it) => (
                <li key={ it.id } className={ `studio__pick ${it.picked ? 'is-picked' : ''}` }>
                  <label className="studio__pick-head">
                    <input type="checkbox" checked={ it.picked } onChange={ () => this.togglePick(it.id) } />
                    <input type="text" className="studio__pick-title" maxLength="60" value={ it.title }
                      aria-label="Ad name" onChange={ (e) => this.editPick(it.id, 'title', e.target.value) } />
                  </label>
                  <textarea className="studio__input studio__pick-brief" rows="3" maxLength="600" value={ it.brief }
                    aria-label="What the ad says" onChange={ (e) => this.editPick(it.id, 'brief', e.target.value) } />
                  { it.until && <span className="studio__hint studio__hint--tight">Plays until { fmtDate(it.until) }</span> }
                </li>
              ))
            }
          </ul>
          <div className="studio__row">
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-voice">Voice</label>
              <select id="studio-pack-voice" className="studio__input" value={ packVoice } onChange={ (e) => this.setState({ packVoice: e.target.value }) }>
                { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
              </select>
            </div>
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-mood">Music</label>
              <select id="studio-pack-mood" className="studio__input" value={ packMood } onChange={ (e) => this.setState({ packMood: e.target.value }) }>
                { MOODS.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
              </select>
            </div>
          </div>
          { studioStore.packError && <div className="studio__error" role="alert">{ studioStore.packError }</div> }
          <button type="button" className="studio__btn studio__btn--primary studio__btn--big"
            disabled={ studioStore.packBusy || count === 0 || count > free }
            onClick={ () => this.makePack() }>
            { count > free ? `Untick ${count - free}: only ${free} free ${free === 1 ? 'slot' : 'slots'}` : studioStore.packBusy ? 'Starting…' : `Make ${count} ${count === 1 ? 'ad' : 'ads'}` }
          </button>
          <button type="button" className="studio__link" onClick={ () => { studioStore.pack = null } }>Read something else instead</button>
        </div>
      )
    }

    // 3. Choose a source
    const failed = pack && pack.status === 'failed'
    const nothing = pack && pack.status === 'done' && (!pack.suggestions || !pack.suggestions.length)
    const canRead = packSource === 'website' ? packWebsite.trim().length > 3
      : packSource === 'doc' ? packDocUrl.trim().length > 10
        : packText.trim().length >= 80
    return (
      <form className="studio__screen" onSubmit={ (e) => this.readPack(e) }>
        <header className="studio__header">
          { back }
          <h2 className="studio__title">Make several ads at once</h2>
        </header>
        <p className="studio__hint">Give us something about your gym and we'll propose the ads. You tick the ones you want, listen, and approve.</p>

        <div className="studio__segmented studio__segmented--loose" role="group" aria-label="Where to read from">
          {
            [['website', 'Website'], ['doc', 'Google Doc'], ['file', 'Word file'], ['text', 'Paste text']].map(([value, label]) => (
              <button key={ value } type="button" aria-pressed={ packSource === value }
                className={ `studio__segment ${packSource === value ? 'is-active' : ''}` }
                onClick={ () => this.setState({ packSource: value }) }>{ label }</button>
            ))
          }
        </div>

        {
          packSource === 'website' && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-website">Your website</label>
              <input id="studio-pack-website" className="studio__input" type="text" autoCapitalize="none" spellCheck="false"
                placeholder="e.g. mygym.ie" value={ packWebsite } onChange={ (e) => this.setState({ packWebsite: e.target.value }) } />
              <span className="studio__hint">We read the home page and the pages about classes, prices and offers.</span>
            </div>
          )
        }
        {
          packSource === 'doc' && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-doc">Google Doc link</label>
              <input id="studio-pack-doc" className="studio__input" type="text" autoCapitalize="none" spellCheck="false"
                placeholder="https://docs.google.com/document/d/…" value={ packDocUrl } onChange={ (e) => this.setState({ packDocUrl: e.target.value }) } />
              <span className="studio__hint">In Google Docs choose Share, then "Anyone with the link" (viewer is enough), and paste the link.</span>
            </div>
          )
        }
        {
          packSource === 'file' && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-file">Word file (.docx) or text file</label>
              <input id="studio-pack-file" className="studio__input studio__file" type="file" accept=".docx,.txt,.md,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={ (e) => this.pickFile(e) } />
              { packFileName && <span className="studio__hint">Read { packFileName }. The file stays on this device; only its text is used.</span> }
              { packFileError && <div className="studio__error" role="alert">{ packFileError }</div> }
            </div>
          )
        }
        {
          packSource === 'text' && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-pack-text">Text about your gym</label>
              <textarea id="studio-pack-text" className="studio__input" rows="7" maxLength="24000"
                placeholder="Paste your newsletter, a price list, this month's offers, your class list… anything a member should hear about."
                value={ packText } onChange={ (e) => this.setState({ packText: e.target.value, packFileName: '' }) } />
            </div>
          )
        }

        { failed && <div className="studio__error" role="alert">{ pack.error }</div> }
        { nothing && <div className="studio__error" role="alert">We couldn't find anything to announce in that. Try another page or paste some text about your offers and classes.</div> }
        { studioStore.packError && <div className="studio__error" role="alert">{ studioStore.packError }</div> }

        <button type="submit" className="studio__btn studio__btn--primary studio__btn--big" disabled={ !canRead || studioStore.packBusy || free < 1 }>
          { free < 1 ? 'No free ad slots: delete an ad first' : studioStore.packBusy ? 'Starting…' : 'Find what to promote' }
        </button>
      </form>
    )
  }

  // ---- Review all drafts at once
  reviewEdit (id, field, value) {
    const reviewEdits = { ...this.state.reviewEdits, [id]: { ...(this.state.reviewEdits[id] || {}), [field]: value } }
    this.setState({ reviewEdits })
  }

  // The draft as shown, with any unsaved edits on top
  reviewItem (d) {
    const e = (this.state.reviewEdits || {})[d.id] || {}
    const item = {
      ...d,
      title: e.title !== undefined ? e.title : (d.title || ''),
      script: e.script !== undefined ? e.script : (d.script || ''),
      voice: e.voice || d.voice || 'Tiffany',
      mood: e.mood || d.mood || 'energetic'
    }
    item.edited = item.script.trim() !== (d.script || '').trim() || item.title.trim() !== (d.title || '').trim() ||
      item.voice !== (d.voice || 'Tiffany') || item.mood !== (d.mood || 'energetic')
    return item
  }

  rerecordChanged () {
    const { studioStore } = this.props
    const changes = studioStore.reviewDrafts.map((d) => this.reviewItem(d))
      .filter((it) => it.edited && it.script.trim().length >= 10)
      .map((it) => ({ id: it.id, script: it.script.trim(), voice: it.voice, mood: it.mood, title: it.title.trim() }))
    if (!changes.length) return
    this.stopPreview()
    const reviewEdits = { ...this.state.reviewEdits }
    changes.forEach((c) => { delete reviewEdits[c.id] })
    this.setState({ reviewEdits })
    studioStore.rerecordMany(changes)
  }

  renderReviewAll () {
    const { studioStore } = this.props
    const { playingKey } = this.state
    const items = studioStore.reviewDrafts.map((d) => this.reviewItem(d))
    const ready = items.filter((it) => it.step === 'ready')
    const changed = items.filter((it) => it.edited && it.script.trim().length >= 10)
    const recording = items.filter((it) => it.step !== 'ready' && it.step !== 'failed')
    const canApproveAll = ready.length > 0 && !changed.length && !recording.length && !studioStore.rerecording
    return (
      <div className="studio__screen">
        <header className="studio__header">
          <button type="button" className="studio__back" aria-label="Back to your ads" onClick={ () => { this.stopPreview(); studioStore.backToList() } }><BackIcon /></button>
          <h2 className="studio__title">Review your drafts</h2>
        </header>
        <p className="studio__hint">Listen to each one and fix any names, times or prices. Change what you like, re-record the changed ones in one go, then approve.</p>

        {
          items.length === 0 && (
            <p className="studio__hint">{ studioStore.drafts.length ? 'Loading your drafts…' : 'No drafts to review.' }</p>
          )
        }

        <ul className="studio__reviews" aria-label="Drafts">
          {
            items.map((it) => {
              const key = `draft-${it.id}-${it.updated}`
              const busy = it.step !== 'ready' && it.step !== 'failed'
              return (
                <li key={ it.id } className={ `studio__review ${it.edited ? 'is-edited' : ''}` }>
                  <div className="studio__player studio__player--tight">
                    <button type="button" className="studio__bigplay" disabled={ busy || !it.audioUrl }
                      aria-label={ playingKey === key ? 'Pause' : `Play ${it.title}` }
                      onClick={ () => this.preview(key, it.audioUrl) }>
                      { playingKey === key ? <PauseIcon /> : <PlayIcon /> }
                    </button>
                    <div className="studio__player-text">
                      <input type="text" className="studio__pick-title" maxLength="60" value={ it.title } aria-label="Ad name"
                        onChange={ (e) => this.reviewEdit(it.id, 'title', e.target.value) } />
                      <span className="studio__item-meta">
                        { busy ? 'Recording…' : it.step === 'failed' ? (it.error || "Couldn't be made") : [fmtDuration(it.duration), it.voice, it.until ? `until ${fmtDate(it.until)}` : ''].filter(Boolean).join(' · ') }
                      </span>
                    </div>
                  </div>
                  <textarea className="studio__input studio__pick-brief" rows="3" maxLength="600" value={ it.script } aria-label="Script"
                    disabled={ busy } onChange={ (e) => this.reviewEdit(it.id, 'script', e.target.value) } />
                  <div className="studio__review-foot">
                    <select className="studio__input studio__input--mini" value={ it.voice } aria-label="Voice" disabled={ busy }
                      onChange={ (e) => this.reviewEdit(it.id, 'voice', e.target.value) }>
                      { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
                    </select>
                    <select className="studio__input studio__input--mini" value={ it.mood } aria-label="Music" disabled={ busy }
                      onChange={ (e) => this.reviewEdit(it.id, 'mood', e.target.value) }>
                      { MOODS.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
                    </select>
                    <span className="studio__review-space" />
                    <button type="button" className="studio__btn studio__btn--ghost studio__btn--small" aria-label={ `Delete ${it.title}` }
                      onClick={ () => { this.stopPreview(); studioStore.archiveFromReview(it.id) } }><TrashIcon /></button>
                    {
                      it.step === 'ready' && !it.edited && (
                        <button type="button" className="studio__btn studio__btn--small studio__btn--primary"
                          onClick={ () => this.requirePro(() => { this.stopPreview(); studioStore.approveOne(it.id) }) }>Approve</button>
                      )
                    }
                    { it.edited && <span className="studio__hint studio__hint--tight">Changed: re-record below</span> }
                  </div>
                </li>
              )
            })
          }
        </ul>

        { studioStore.error && <div className="studio__error" role="alert">{ studioStore.error }</div> }

        {
          changed.length > 0 && (
            <button type="button" className="studio__btn studio__btn--ghost studio__btn--big" disabled={ studioStore.rerecording }
              onClick={ () => this.rerecordChanged() }>
              { studioStore.rerecording ? 'Re-recording…' : `Re-record ${changed.length} changed ${changed.length === 1 ? 'ad' : 'ads'}` }
            </button>
          )
        }
        {
          items.length > 0 && (
            <button type="button" className="studio__btn studio__btn--primary studio__btn--big" disabled={ !canApproveAll || studioStore.approvingAll }
              onClick={ () => this.requirePro(() => { this.stopPreview(); studioStore.approveAll() }) }>
              { studioStore.approvingAll ? 'Approving…'
                : changed.length ? 'Re-record your changes first'
                  : recording.length ? `Recording ${recording.length}…`
                    : `Approve all ${ready.length} and go live` }
            </button>
          )
        }
      </div>
    )
  }

  renderCreate () {
    const { studioStore } = this.props
    const { details, website, voice, mood, until } = this.state
    return (
      <form className="studio__screen" onSubmit={ (e) => this.submit(e) }>
        <header className="studio__header">
          <button type="button" className="studio__back" aria-label="Back to your ads" onClick={ () => studioStore.backToList() }><BackIcon /></button>
          <h2 className="studio__title">Create an ad</h2>
        </header>

        <div className="studio__group">
          <label className="studio__label" htmlFor="studio-details">What do you want to promote?</label>
          <textarea id="studio-details" className="studio__input" rows="4" maxLength="1200" required
            value={ details }
            placeholder="e.g. Bring a friend for free this weekend. Or: new opening hours from Monday, 6am to 10pm."
            onChange={ (e) => this.setState({ details: e.target.value }) } />
          <span className="studio__hint">Write it like you'd tell a member. We'll turn it into a 15-second ad.</span>
          <span className="studio__hint">{ `Ad ${Math.min(studioStore.made + 1, studioStore.limit)} of ${studioStore.limit}. Re-recording is unlimited, and deleting an ad frees a slot.` }</span>
        </div>

        <div className="studio__group">
          <label className="studio__label" htmlFor="studio-website">Your website or gym name</label>
          <input id="studio-website" className="studio__input" type="text" autoComplete="organization"
            autoCapitalize="none" spellCheck="false" placeholder="e.g. mygym.ie or Iron Works Gym"
            value={ website } onChange={ (e) => this.setState({ website: e.target.value }) } />
          <span className="studio__hint">Saved once. From a website we take your gym's name, classes and tone; no website is fine too.</span>
        </div>

        <div className="studio__row">
          <div className="studio__group">
            <label className="studio__label" htmlFor="studio-voice">Voice</label>
            <select id="studio-voice" className="studio__input" value={ voice } onChange={ (e) => this.setState({ voice: e.target.value }) }>
              { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
            </select>
          </div>
          <div className="studio__group">
            <label className="studio__label" htmlFor="studio-until">Runs until</label>
            <input id="studio-until" className="studio__input" type="date" min={ inDays(0) }
              value={ until } onChange={ (e) => this.setState({ until: e.target.value }) } />
          </div>
        </div>

        <div className="studio__group">
          <span className="studio__label">Music</span>
          <div className="studio__segmented studio__segmented--loose" role="group" aria-label="Music mood">
            {
              MOODS.map(([value, label]) => (
                <button key={ value } type="button" aria-pressed={ mood === value }
                  className={ `studio__segment ${mood === value ? 'is-active' : ''}` }
                  onClick={ () => this.setState({ mood: value }) }>{ label }</button>
              ))
            }
          </div>
        </div>

        { studioStore.error && <div className="studio__error" role="alert">{ studioStore.error }</div> }

        <button type="submit" className="studio__btn studio__btn--primary studio__btn--big" disabled={ details.trim().length < 10 }>
          Create my ad
        </button>
      </form>
    )
  }

  renderMaking () {
    const { studioStore } = this.props
    const step = (studioStore.draft && studioStore.draft.step) || 'queued'
    const at = STEPS.findIndex((s) => s[0] === step)
    // Re-records skip reading/writing
    return (
      <div className="studio__screen">
        <header className="studio__header">
          <button type="button" className="studio__back" aria-label="Back to your ads" onClick={ () => studioStore.backToList() }><BackIcon /></button>
          <h2 className="studio__title">Making your ad…</h2>
        </header>
        <p className="studio__hint">Usually ready in under a minute. You can keep using the player, and find the draft in your ads list.</p>
        <ol className="studio__steps">
          {
            STEPS.map(([key, label], i) => {
              const done = at > i
              const active = at === i || (at < 0 && i === 0)
              return (
                <li key={ key } className={ `studio__step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}` }>
                  <span className="studio__step-dot" aria-hidden="true">{ done ? '✓' : '' }</span>
                  <span>{ label }</span>
                </li>
              )
            })
          }
        </ol>
      </div>
    )
  }

  renderReview () {
    const { studioStore } = this.props
    const { script, playingKey } = this.state
    const d = studioStore.draft || {}
    const title = this.state.title !== undefined && this.state.title !== null ? this.state.title : (d.title || '')
    const reviewVoice = this.state.reviewVoice || d.voice || 'Tiffany'
    const reviewMood = this.state.reviewMood || d.mood || 'energetic'
    const failed = d.step === 'failed'
    const live = d.status === 'live'
    const key = `draft-${d.id}-${d.updated}`
    const edited = script.trim() !== (d.script || '').trim() ||
      title.trim() !== (d.title || '').trim() ||
      reviewVoice !== (d.voice || 'Tiffany') || reviewMood !== (d.mood || 'energetic')
    return (
      <div className="studio__screen">
        <header className="studio__header">
          <button type="button" className="studio__back" aria-label="Back to your ads" onClick={ () => { this.stopPreview(); studioStore.backToList() } }><BackIcon /></button>
          <h2 className="studio__title">{ failed ? "That didn't work" : live ? 'Your ad' : 'Your draft is ready' }</h2>
          { !failed && <span className={ `studio__badge ${live ? 'studio__badge--live' : ''}` }>{ live ? 'Live' : 'Draft' }</span> }
        </header>
        {
          live && (
            <p className="studio__hint">This ad is playing in your ad breaks. If you change the name, script, voice or music and re-record it, it comes off air until you approve the new version.</p>
          )
        }

        {
          failed ? (
            <div className="studio__error" role="alert">{ d.error || 'Something went wrong making this ad.' }</div>
          ) : (
            <div className="studio__player">
              <button type="button" className="studio__bigplay"
                aria-label={ playingKey === key ? 'Pause preview' : 'Play preview' }
                onClick={ () => this.preview(key, d.audioUrl) }>
                { playingKey === key ? <PauseIcon /> : <PlayIcon /> }
              </button>
              <div className="studio__player-text">
                <span className="studio__item-title">{ d.title }</span>
                <span className="studio__item-meta">{ [fmtDuration(d.duration), d.voice, (d.mood || '').replace(/^./, (c) => c.toUpperCase())].filter(Boolean).join(' · ') }</span>
              </div>
            </div>
          )
        }

        {
          !failed && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-title">Name</label>
              <input id="studio-title" className="studio__input" type="text" maxLength="60" value={ title }
                placeholder="e.g. New HIIT class"
                onChange={ (e) => this.setState({ title: e.target.value }) } />
              <span className="studio__hint">How the ad is listed. Members never hear it.</span>
            </div>
          )
        }

        {
          d.script !== undefined && (
            <div className="studio__group">
              <label className="studio__label" htmlFor="studio-script">Script</label>
              <textarea id="studio-script" className="studio__input" rows="5" maxLength="600"
                value={ script } onChange={ (e) => this.setState({ script: e.target.value }) } />
              <span className="studio__hint">Check names, times and prices. Edit any word, or change the voice or music, and re-record it. "Try another version" writes a new script with a different voice and music.</span>
            </div>
          )
        }

        {
          !failed && (
            <div className="studio__row">
              <div className="studio__group">
                <label className="studio__label" htmlFor="studio-review-voice">Voice</label>
                <select id="studio-review-voice" className="studio__input" value={ reviewVoice }
                  onChange={ (e) => this.setState({ reviewVoice: e.target.value }) }>
                  { VOICES.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
                </select>
              </div>
              <div className="studio__group">
                <label className="studio__label" htmlFor="studio-review-mood">Music</label>
                <select id="studio-review-mood" className="studio__input" value={ reviewMood }
                  onChange={ (e) => this.setState({ reviewMood: e.target.value }) }>
                  { MOODS.map(([v, label]) => <option key={ v } value={ v }>{ label }</option>) }
                </select>
              </div>
            </div>
          )
        }

        { studioStore.error && <div className="studio__error" role="alert">{ studioStore.error }</div> }

        <div className="studio__row">
          <button type="button" className="studio__btn studio__btn--ghost" disabled={ !edited || script.trim().length < 10 }
            onClick={ () => { this.stopPreview(); studioStore.rerecord(script, reviewVoice, reviewMood, title.trim()) } }>Re-record changes</button>
          <button type="button" className="studio__btn studio__btn--ghost"
            onClick={ () => { this.stopPreview(); studioStore.regenerate() } }>Try another version</button>
        </div>

        {
          !failed && (
            <React.Fragment>
              <p className="studio__hint">{ d.until ? `Plays until ${fmtDate(d.until)}, then switches off by itself.` : 'Plays until you switch it off.' }</p>
              {
                !live && (
                  <button type="button" className="studio__btn studio__btn--primary studio__btn--big" disabled={ edited }
                    onClick={ () => { this.stopPreview(); studioStore.approve() } }>
                    { edited ? 'Re-record your changes first' : 'Approve and go live' }
                  </button>
                )
              }
              <button type="button" className="studio__link" onClick={ () => { this.stopPreview(); studioStore.archive(d.id); studioStore.backToList() } }>
                { live ? 'Delete this ad' : 'Delete this draft' }
              </button>
            </React.Fragment>
          )
        }
      </div>
    )
  }

  render () {
    const { studioStore } = this.props
    let body
    switch (studioStore.screen) {
      case 'create': body = this.renderCreate(); break
      case 'making': body = this.renderMaking(); break
      case 'review': body = this.renderReview(); break
      case 'pack': body = this.renderPack(); break
      case 'reviewAll': body = this.renderReviewAll(); break
      default: body = this.renderList()
    }
    return <section className="studio" aria-label="Ads and announcements" ref={ (el) => { this.root = el } }>{ body }</section>
  }
}

export default inject((stores) => ({
  studioStore: stores.studioStore,
  audioMessageStore: stores.audioMessageStore,
  playlistStore: stores.playlistStore,
  userStore: stores.userStore
}))(observer(AdsPanel))
